import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import classNames from 'classnames';
import { ProductType } from '../../../clients/quipli-service/product/product';
import { Location } from '../../../clients/quipli-service/location/location';
import { OrderSection } from '../../order/order-section/order-section';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { Field, FieldCounter } from '../../../components/field/field';
import { PurchaseOrderProductLineItem } from '../../../clients/quipli-service/purchase-order/purchase-order';
import { ProductAutoComplete } from '../../../components/product-auto-complete/product-auto-complete';
import { CustomLineItemForm } from './custom-line-item-form';
import { usePurchaseOrderItemForm } from '../hooks/use-purchase-order-item-form';

export interface PurchaseLineItemFormProps {
  location?: Location;
  openOnStart?: boolean;
  onAddItem: (item: PurchaseOrderProductLineItem) => void;
}

export const PurchaseLineItemForm = ({ location, openOnStart = true, onAddItem }: PurchaseLineItemFormProps) => {
  const {
    // Refs
    collapsibleRef,

    // Helpers
    isDisabled,

    // States
    productType,
    price,
    qty,
    product,
    name,
    description,
    generalLedgerAccount,
    accounts,
    errors,

    // Setters
    setPrice,
    setQty,
    setProduct,
    setName,
    setDescription,
    setGeneralLedgerAccount,

    // Callbacks
    collapseAndClear,
    submit,
    changeProductType,
  } = usePurchaseOrderItemForm({ onAddItem, openOnStart });

  return (
    <div className="order-add-product">
      <OrderSection>
        <Collapsible
          ref={collapsibleRef}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography
                type="text-md"
                weight="medium"
                className="text-grey-800"
                data-cy="add_products_to_order_title"
              >
                Add products to order
              </Typography>
              <Icon
                icon={isOpen ? 'Close' : 'Plus'}
                stroke="var(--quipli-black-800)"
                data-cy="collapsible-icon"
                data-open={isOpen}
              />
            </div>
          )}
        >
          <div className="flex flex-col gap-3">
            <div className="list-tabs">
              <div className="tabs-wrapper">
                <div
                  className={classNames('tab-item', { active: productType === ProductType.Product })}
                  onClick={() => changeProductType(ProductType.Product)}
                  data-cy="product_tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={productType === ProductType.Product ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Product
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: productType === ProductType.Part })}
                  onClick={() => {
                    changeProductType(ProductType.Part);
                  }}
                  data-cy="part-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={productType === ProductType.Part ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Part
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: productType === ProductType.Custom })}
                  onClick={() => {
                    changeProductType(ProductType.Custom);
                  }}
                  data-cy="custom-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={productType === ProductType.Custom ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Other
                  </Typography>
                  <div className="divider-active"></div>
                </div>
              </div>
            </div>

            {productType === ProductType.Custom ? (
              <CustomLineItemForm
                description={description}
                setDescription={setDescription}
                name={name}
                setName={setName}
                generalLedgerAccount={generalLedgerAccount}
                setGeneralLedgerAccount={setGeneralLedgerAccount}
                accounts={accounts}
                errors={errors}
              />
            ) : (
              <ProductAutoComplete
                types={[productType]}
                onSelect={(p) => setProduct(p)}
                value={product}
                locationIds={location ? [location.id] : undefined}
              />
            )}

            <div className="flex flex-col md:flex-row gap-[12px] justify-between mb-3">
              <Field
                label="Price"
                isRequired
                type="currency"
                value={price}
                onChange={(v) => setPrice(v)}
                maximumFractionDigits={2}
                invalid={!!errors.price}
                description={errors.price}
                dataCy="field_price"
              />

              <FieldCounter
                label="Quantity"
                min={1}
                value={qty}
                onChange={(v) => setQty(v)}
                invalid={!!errors.qty}
                description={errors.qty}
                dataCy="field_quantity"
                isRequired
              />
            </div>

            <div className="button-group">
              <Button
                className="button-secondary"
                onClick={() => collapseAndClear()}
                data-cy="cancel_add_product_to_order"
              >
                Cancel
              </Button>
              <Button className="button-primary" onClick={submit} disabled={isDisabled} data-cy="add_product_to_order">
                Add to order
              </Button>
            </div>
          </div>
        </Collapsible>
      </OrderSection>
    </div>
  );
};
